import { useRef } from 'react';
import { useSelector } from 'react-redux';
import { Flex, Input as ChakraInput } from '@chakra-ui/react';
import { Chip } from './Chip';

const mergeRefs =
    (...refs) =>
    (node) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
        });
    };

/*
 * Inline tag input: committed chips render inside the same bordered box as
 * the text caret (Gmail "To:"-field style), replacing the old two-part
 * "ChipList above, plain Input below" v4 pairing and material-ui-chip-input's
 * ChipInput. All commit/delimiter/paste/validation logic stays with the
 * caller — this component only renders chips + the buffer input inline and
 * forwards DOM events, mirroring the existing dumb-presentational split
 * between ChipInput/ChipList and their callers' hooks.
 */
const TagInput = ({
    id,
    chips = [],
    onRemoveChip,
    isDisabled,
    isInvalid,
    inputValue,
    onInputChange,
    onKeyDown,
    onPaste,
    onFocus,
    onBlur,
    onSelect,
    placeholder,
    inputRef,
    inputProps,
    ...rest
}) => {
    const { darkMode } = useSelector((state) => state.settings);
    const localRef = useRef(null);

    return (
        <Flex
            wrap="wrap"
            align="center"
            gap={1}
            p={1}
            border="1px solid"
            borderRadius="md"
            borderColor={
                isInvalid
                    ? 'red.500'
                    : darkMode
                      ? 'dark.tcap-borderGray'
                      : 'inherit'
            }
            bg={darkMode ? 'dark.bgDark' : undefined}
            opacity={isDisabled ? 0.5 : 1}
            onClick={() => localRef.current?.focus()}
            {...rest}>
            {chips.map((chip, index) => (
                <Chip
                    key={`${chip}-${index}`}
                    input={chip}
                    index={index}
                    isDisabled={isDisabled}
                    onCloseClick={onRemoveChip}
                />
            ))}
            <ChakraInput
                id={id}
                ref={mergeRefs(localRef, inputRef)}
                variant="unstyled"
                px={1}
                flex="1"
                minW="120px"
                isDisabled={isDisabled}
                value={inputValue}
                onChange={onInputChange}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                onFocus={onFocus}
                onBlur={onBlur}
                onSelect={onSelect}
                placeholder={chips.length === 0 ? placeholder : ''}
                {...inputProps}
            />
        </Flex>
    );
};

export default TagInput;
